import { Picture } from "@/components/ui/Picture";
import Video from "@/components/ui/Video";
import { MediaElementStoryblok } from "@/generated/sb/sb-types";
import FormatStoryblokMedia, {
  FormattedMedia,
} from "@/lib/storyblok/utils/Media/FormatStoryblokMedia";
import { getStreamVideoUrl } from "@/lib/utils/video";
import styles from "./Asset.module.css";
/**
 * Produces media sizes for mobile first
 * @example
 * sizes(100, 50, 25)
 * // 100vw, (min-width: 768px) 50vw, (min-width: 1024px) 25vw
 *
 * @description this is used to produce the sizes attribute for the Image component
 */
export const sizes = (main: number, md?: number, lg?: number) => {
  const sizes: string[] = [];

  if (lg) {
    sizes.push(`(min-width: 1024px) ${lg}vw`);
  }

  if (md) {
    sizes.push(`(min-width: 768px) ${md}vw`);
  }

  sizes.push(`${main}vw`);

  return sizes.join(", ");
};

export interface AssetProps {
  src: MediaElementStoryblok;
  sizes?: string;
  alt?: string;

  autoPlay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  priority?: boolean;
  quality?: number;
  audio?: boolean;
}

/**
 * Asset component - uses FILL by default.
 * To use this component you must provide a container with the valid dimensions / aspect-ratio
 */
export const Asset = ({
  src,
  sizes,
  autoPlay,
  loop,
  muted,
  priority,
  quality,
  audio,
}: AssetProps) => {
  const { mobile, desktop } = FormatStoryblokMedia(src);
  if (!mobile || !desktop) return null;

  if (mobile.isImage && desktop.isImage && (mobile.file || desktop.file)) {
    return (
      <Picture
        src={mobile.file ? mobile.file : desktop.file}
        media={{
          "(min-width: 768px)": desktop.file ? desktop.file! : mobile.file!,
        }}
        focus={desktop?.focus}
        alt={desktop?.alt}
        quality={quality}
        sizes={sizes || "100vw"}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fill
        fetchPriority={priority ? "high" : "auto"}
      />
    );
  }

  const assetProps = { autoPlay, loop, muted, priority, audio };

  return (
    <>
      <AssetRenderer {...assetProps} src={desktop} className={styles.desktop} />
      <AssetRenderer
        {...assetProps}
        src={mobile?.file ? mobile : desktop}
        className={styles.mobile}
      />
    </>
  );
};

const AssetRenderer = ({
  src,
  sizes,
  quality,
  priority,
  className,
  autoPlay,
  audio,
  loop,
  muted = true,
}: {
  src: FormattedMedia["desktop"];
  sizes?: string;
  quality?: number;
  priority?: boolean;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  audio?: boolean;
}) => {
  if (src?.isImage && src?.file) {
    return (
      <Picture
        className={className}
        focus={src?.focus}
        alt={src?.alt}
        quality={quality}
        sizes={sizes || "100vw"}
        loading={priority ? "eager" : "lazy"}
        decoding="auto"
        fill
        fetchPriority={priority ? "high" : "auto"}
        src={src.file}
      />
    );
  }

  if (src?.isVideo) {
    return (
      <Video
        width={"100%"}
        height={"100%"}
        url={getStreamVideoUrl(src.file)}
        controls={false}
        className={className}
        {...{ autoPlay, loop, muted, sizes }}
        audio={audio}
      />
    );
  }

  return null;
};
